import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { PersonalData, PersonalDataValidation } from "../../../../types/bookingCreation";
import { Input } from "components/ui/input";
import PhoneNumberInput from "components/ui/phone-input";
import { RootState } from "../../../../redux/store";
import { useAppDispatch } from "../../../../redux/useDispatch";
import {
	recipientEmailChanged,
	recipientFirstNameChanged,
	recipientLastNameChanged,
	recipientPhoneChanged,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { useKeyHandoverStepCounter } from "../hooks/useKeyHandoverStepCounter";
import { useEmailFieldFeedback } from "../../common/hooks/useEmailFieldFeedback";

const KeyHandoverGuestDetailsStep = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { current, total } = useKeyHandoverStepCounter("guest");
  const recipientData: PersonalData = useSelector(
    (state: RootState) => state.bookingCreation.recipientData
  );
  const recipientDataValidation: PersonalDataValidation = useSelector(
    (state: RootState) => state.bookingCreation.recipientDataValidation
  );

  // Show validation feedback only after user interaction
  const [touched, setTouched] = React.useState({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
  });

  const markTouched = (field: keyof typeof touched) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const onFirstNameChange = (firstName: string) =>
    dispatch(recipientFirstNameChanged({ firstName }));
  const onLastNameChange = (lastName: string) =>
    dispatch(recipientLastNameChanged({ lastName }));
  const onPhoneChange = (phone: string) =>
    dispatch(recipientPhoneChanged({ phone }));
  const onEmailChange = (email: string) =>
    dispatch(recipientEmailChanged({ email }));

  const emailFeedback = useEmailFieldFeedback(recipientData.email, touched.email);

  // Optional fields: only show phone error when non-empty and invalid
  const phoneInvalid =
    recipientData.phone !== "" && !recipientDataValidation.phone;

  return (
    <div className="w-full flex flex-col items-center">
      {/* Sub headline: Step indicator under main header */}
      <p className="-mt-5 mb-6 text-xs text-muted-foreground text-center w-full" role="status" aria-live="polite">
        {t("book_location.keys.step_counter", {
          current,
          total,
          defaultValue: "Schritt {{current}} von {{total}}",
        })}
      </p>
      <div className="w-full max-w-md mx-auto space-y-4 pb-4">
        {/* First name (required) */}
        <div className="space-y-1">
          <label htmlFor="recipient-first-name" className="text-sm font-medium">
            {t("book_location.personal_data.first_name")}<span aria-hidden="true" className="text-appRed ml-0.5">*</span>
          </label>
          {/** Compute invalid flags for clearer semantics and to avoid duplicated expressions */}
          {/* eslint-disable-next-line */}
          {(() => null)()}
          {/** end helper */}
          <Input
            id="recipient-first-name"
            value={recipientData.firstName}
            onChange={(e) => onFirstNameChange(e.target.value)}
            onBlur={() => markTouched("firstName")}
            autoComplete="given-name"
            required
            aria-required="true"
            error={touched.firstName && !recipientDataValidation.firstName}
            aria-invalid={touched.firstName && !recipientDataValidation.firstName}
            aria-describedby={
              touched.firstName && !recipientDataValidation.firstName
                ? "recipient-first-name-error"
                : undefined
            }
          />
          {touched.firstName && !recipientDataValidation.firstName && (
            <p id="recipient-first-name-error" className="text-sm text-appRed mt-1">
              {t("book_location.recipient_data.validation.first_name_required", {
                defaultValue: "Bitte gib den Vornamen des Gastes ein.",
              })}
            </p>
          )}
        </div>

        {/* Last name (required) */}
        <div className="space-y-1">
          <label htmlFor="recipient-last-name" className="text-sm font-medium">
            {t("book_location.personal_data.last_name")}<span aria-hidden="true" className="text-appRed ml-0.5">*</span>
          </label>
          <Input
            id="recipient-last-name"
            value={recipientData.lastName}
            onChange={(e) => onLastNameChange(e.target.value)}
            onBlur={() => markTouched("lastName")}
            autoComplete="family-name"
            required
            aria-required="true"
            error={touched.lastName && !recipientDataValidation.lastName}
            aria-invalid={touched.lastName && !recipientDataValidation.lastName}
            aria-describedby={
              touched.lastName && !recipientDataValidation.lastName
                ? "recipient-last-name-error"
                : undefined
            }
          />
          {touched.lastName && !recipientDataValidation.lastName && (
            <p id="recipient-last-name-error" className="text-sm text-appRed mt-1">
              {t("book_location.recipient_data.validation.last_name_required", {
                defaultValue: "Bitte gib den Nachnamen des Gastes ein.",
              })}
            </p>
          )}
        </div>

        {/* Email (required) */}
        <div className="space-y-1">
          <label htmlFor="recipient-email" className="text-sm font-medium">
            {t("book_location.personal_data.email")}<span aria-hidden="true" className="text-appRed ml-0.5">*</span>
          </label>
          <Input
            id="recipient-email"
            type="email"
            value={recipientData.email}
            onChange={(e) => onEmailChange(e.target.value)}
            onBlur={() => markTouched("email")}
            autoComplete="email"
            required
            aria-required="true"
            error={!!emailFeedback.error}
            aria-invalid={!!emailFeedback.error}
            aria-describedby={
              emailFeedback.error
                ? "recipient-email-error"
                : emailFeedback.warning
                ? "recipient-email-warning"
                : undefined
            }
          />
          {emailFeedback.error && (
            <p id="recipient-email-error" className="text-sm text-appRed mt-1">
              {emailFeedback.error}
            </p>
          )}
          {emailFeedback.warning && (
            <p id="recipient-email-warning" className="text-sm text-slate-600 mt-1">
              {emailFeedback.warning}
            </p>
          )}
        </div>

        {/* Phone (optional) */}
        <div className="space-y-1">
          <label htmlFor="recipient-phone" className="text-sm font-medium">
            {t("book_location.personal_data.phone_number")}
          </label>
          <PhoneNumberInput
            id="recipient-phone"
            value={recipientData.phone}
            onChange={(val) => onPhoneChange(val)}
            onBlur={() => markTouched("phone")}
            autoComplete="tel"
            error={touched.phone && phoneInvalid}
            aria-invalid={touched.phone && phoneInvalid}
            aria-describedby={touched.phone && phoneInvalid ? "recipient-phone-error" : undefined}
          />
          {touched.phone && phoneInvalid && (
            <p id="recipient-phone-error" className="text-sm text-appRed mt-1">
              {t("book_location.recipient_data.validation.phone_invalid", {
                defaultValue: "Bitte gib eine gültige Telefonnummer des Gastes ein.",
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default KeyHandoverGuestDetailsStep;
